import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, MenuItem,
  IconButton, Collapse, Chip, Button, Pagination,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { sendServerRequest } from '../../server/request';
import { openGlobalSnack } from '../utils/snack';
import { CircularLoading } from '../utils/loading';

const ENDPOINT = '/server/userLeads/clt/logs';
const PAGE_SIZE = 20;

type LeadLog = {
  id: string;
  lead_id: number | null;
  user_id: number | null;
  cpf: string | null;
  stage: string;
  provider: string | null;
  reason: string | null;
  status: number | null;
  message: string;
  response: string | null;
  createdAt: string;
};

const STAGE_LABELS: Record<string, { label: string; color: 'info' | 'warning' | 'default' }> = {
  consult: { label: 'Consulta', color: 'info' },
  dispatch: { label: 'Disparo', color: 'warning' },
};

function stageChip(stage: string) {
  return STAGE_LABELS[stage] ?? { label: stage, color: 'default' as const };
}

const PROVIDER_LABELS: Record<string, string> = {
  v8: 'V8',
  suma_celcoin: 'Suma · Celcoin',
  suma_uy3: 'Suma · UY3',
};

function providerLabel(name: string | null) {
  if (!name) return '—';
  return PROVIDER_LABELS[name] ?? name;
}

function fmtDate(iso: string) {
  try { return new Date(iso).toLocaleString('pt-BR'); } catch { return iso; }
}

function prettyJson(raw: string | null) {
  if (!raw) return '';
  try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
}

export function FormLeadLogsPanel({ focusLeadId, focusNonce }: { focusLeadId?: string; focusNonce?: number } = {}) {
  const [logs, setLogs] = useState<LeadLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [stage, setStage] = useState('');
  const [provider, setProvider] = useState('');
  const [cpf, setCpf] = useState('');
  const [leadId, setLeadId] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link da tabela de leads (botão "Erros"): cada clique bumpa o focusNonce
  // → aplica o leadId e recarrega. Digitar no campo NÃO recarrega (clica Filtrar).
  useEffect(() => {
    if (focusNonce && focusLeadId !== undefined) {
      setLeadId(focusLeadId);
      load(1, { leadId: focusLeadId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusNonce]);

  function load(toPage: number, override?: { leadId?: string }) {
    setLoading(true);
    const q = new URLSearchParams({ page: String(toPage), pageSize: String(PAGE_SIZE) });
    if (stage) q.set('stage', stage);
    if (provider) q.set('provider', provider);
    if (cpf.trim()) q.set('cpf', cpf.trim());
    const useLeadId = override?.leadId !== undefined ? override.leadId : leadId;
    if (useLeadId.trim()) q.set('leadId', useLeadId.trim());
    sendServerRequest('GET', `${ENDPOINT}?${q.toString()}`)
      .then((e) => {
        const data = e.data?.data ?? {};
        setLogs(data.logs ?? []);
        setTotal(data.total ?? 0);
        setPage(toPage);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        openGlobalSnack('Não foi possível carregar os logs do formulário.', 'error');
      });
  }

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div style={{ maxWidth: 1000, margin: '40px auto 0' }}>
      <h3 style={{ marginBottom: 4, textAlign: 'center' }}>Logs do formulário (consulta → disparo)</h3>
      <p style={{ fontSize: 12, color: '#000000a0', marginTop: 0, marginBottom: 16, textAlign: 'center' }}>
        Erros/falhas de cada lead, da consulta de margem até o disparo do WhatsApp.
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12, justifyContent: 'center' }}>
        <TextField select size="small" label="Etapa" value={stage} style={{ minWidth: 140 }}
          onChange={(e) => setStage(e.target.value)}>
          <MenuItem value="">Todas</MenuItem>
          <MenuItem value="consult">Consulta</MenuItem>
          <MenuItem value="dispatch">Disparo</MenuItem>
        </TextField>
        <TextField select size="small" label="Provider" value={provider} style={{ minWidth: 160 }}
          onChange={(e) => setProvider(e.target.value)}>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="v8">V8</MenuItem>
          <MenuItem value="suma_celcoin">Suma · Celcoin</MenuItem>
          <MenuItem value="suma_uy3">Suma · UY3</MenuItem>
        </TextField>
        <TextField size="small" label="CPF" value={cpf} onChange={(e) => setCpf(e.target.value)} />
        <TextField size="small" label="Lead ID" value={leadId} onChange={(e) => setLeadId(e.target.value)} style={{ width: 110 }} />
        <Button variant="contained" onClick={() => load(1)}>Filtrar</Button>
        {leadId.trim() && <Button variant="text" onClick={() => { setLeadId(''); load(1, { leadId: '' }); }}>Limpar lead</Button>}
        <IconButton onClick={() => load(page)}><RefreshIcon /></IconButton>
      </div>

      {loading ? <CircularLoading /> : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Data</TableCell>
                <TableCell>Etapa</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>CPF</TableCell>
                <TableCell>Motivo</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Mensagem</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center" style={{ color: '#00000080' }}>Nenhum log.</TableCell></TableRow>
              )}
              {logs.map((l) => (
                <React.Fragment key={l.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => setExpanded(expanded === l.id ? null : l.id)}>
                        {expanded === l.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDate(l.createdAt)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={stageChip(l.stage).label}
                        color={stageChip(l.stage).color} variant="outlined" />
                    </TableCell>
                    <TableCell>{providerLabel(l.provider)}</TableCell>
                    <TableCell>{l.cpf ?? '—'}</TableCell>
                    <TableCell>{l.reason ?? '—'}</TableCell>
                    <TableCell>{l.status ?? '—'}</TableCell>
                    <TableCell style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {l.message}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} style={{ paddingTop: 0, paddingBottom: 0, border: 0 }}>
                      <Collapse in={expanded === l.id} unmountOnExit>
                        <div style={{ padding: 12, background: '#00000008' }}>
                          <div style={{ fontSize: 12, marginBottom: 8 }}>
                            <b>lead_id:</b> {l.lead_id ?? '—'} &nbsp;|&nbsp;
                            <b>Motivo:</b> {l.reason ?? '—'} &nbsp;|&nbsp;
                            <b>Status:</b> {l.status ?? '—'}
                          </div>
                          <div style={{ fontSize: 12, marginBottom: 4 }}><b>Mensagem:</b> {l.message}</div>
                          <b style={{ fontSize: 12 }}>Erro / resposta:</b>
                          <pre style={{
                            fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                            maxHeight: 260, overflow: 'auto', margin: '4px 0 0', background: '#0000000a', padding: 8, borderRadius: 4,
                          }}>{prettyJson(l.response) || '—'}</pre>
                        </div>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
          <Pagination count={pages} page={page} onChange={(_, p) => load(p)} />
        </div>
      )}
    </div>
  );
}
